import { useEffect, useRef } from 'react';

interface UseRenameInputFocusOptions {
  /** Whether the rename input is currently active. */
  isEditing: boolean;
  /** Called with the chat ID when a click-outside occurs while the input is focused. */
  onSubmit: (chatId: string) => void;
  /** The ID of the chat being renamed — used to scope the submit callback. */
  chatId: string;
}

/**
 * Manages focus acquisition, selection, and click-outside submission for an
 * inline rename input that replaces a Radix dropdown/context menu trigger.
 *
 * Radix (and other overlay primitives) defer focus restoration and aria-hidden
 * management through chained setTimeout calls after the menu closes. This hook
 * polls until the input holds focus across two consecutive checks, then arms
 * the click-outside listener. Post-arming, it continues polling briefly to
 * recover from late focus theft (e.g. aria-hidden applied after stabilisation).
 *
 * The `armedRef` is exposed so consumers can gate Enter-key submission,
 * preventing leaked keydown events from the closing dropdown.
 */
export function useRenameInputFocus({
  isEditing,
  onSubmit,
  chatId,
}: UseRenameInputFocusOptions) {
  const inputRef = useRef<HTMLInputElement>(null);
  const armedRef = useRef(false);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  useEffect(() => {
    if (!isEditing) return;

    let listenerRegistered = false;
    let pollTimerId: ReturnType<typeof setTimeout>;

    const handleMouseDown = (e: MouseEvent) => {
      const input = inputRef.current;
      if (!input) return;

      const wrapper = input.parentElement;
      const target = e.target as Node;
      const inside = input.contains(target) || wrapper?.contains(target);

      if (inside) return;

      // If the input doesn't currently have focus, something (like aria-hidden
      // on an ancestor) stole it. Re-focus instead of treating as click-outside.
      if (document.activeElement !== input) {
        e.preventDefault();
        input.focus();
        input.select();
        return;
      }

      onSubmitRef.current(chatId);
    };

    let attempts = 0;
    let focusConfirmedOnce = false;
    const maxAttempts = 20;
    const postArmChecks = 6;
    let postArmCount = 0;
    let isArmed = false;

    const tryFocus = () => {
      attempts++;
      const hasFocus = document.activeElement === inputRef.current;

      if (!isArmed) {
        if (hasFocus && focusConfirmedOnce) {
          inputRef.current?.select();
          document.addEventListener('mousedown', handleMouseDown);
          listenerRegistered = true;
          isArmed = true;
          armedRef.current = true;
          pollTimerId = setTimeout(tryFocus, 50);
          return;
        }

        if (hasFocus) {
          focusConfirmedOnce = true;
        } else {
          focusConfirmedOnce = false;
          inputRef.current?.focus();
        }

        if (attempts < maxAttempts) {
          pollTimerId = setTimeout(tryFocus, 50);
        } else {
          inputRef.current?.focus();
          inputRef.current?.select();
          document.addEventListener('mousedown', handleMouseDown);
          listenerRegistered = true;
          isArmed = true;
          armedRef.current = true;
        }
      } else {
        postArmCount++;
        if (!hasFocus) {
          inputRef.current?.focus();
          inputRef.current?.select();
        }
        if (postArmCount < postArmChecks) {
          pollTimerId = setTimeout(tryFocus, 50);
        }
      }
    };

    armedRef.current = false;
    pollTimerId = setTimeout(tryFocus, 30);

    return () => {
      armedRef.current = false;
      clearTimeout(pollTimerId);
      if (listenerRegistered) {
        document.removeEventListener('mousedown', handleMouseDown);
      }
    };
  }, [isEditing, chatId]);

  return { inputRef, armedRef };
}
